import i18n from '@/i18n';
import { getWorkflowTaskResultEntries, resolveWorkflowTaskDisplayStatus, countWorkflowTaskOutcome } from '@/pages/ipe/workbench/constants/workflowTaskQueue';
import { resolveTemplateStepStages } from './workflowStepPlan';

function tp(key, options) {
  return i18n.t(`workbench:taskProgress.${key}`, options);
}

function formatLocalizedCount(n) {
  const v = Number(n) || 0;
  return v.toLocaleString(i18n.resolvedLanguage || i18n.language || 'zh-CN');
}

/** 任务是否处于进行中（含排队） */
export function isTaskInProgress(status) {
  const s = String(status || '').toLowerCase();
  return ['waiting', 'executing', 'processing', 'pending', 'new', 'running', 'queued'].includes(s);
}

export function normalizeTaskCounters(task) {
  const totalRaw = Number(task?.total ?? task?.count ?? 0);
  const total = Number.isFinite(totalRaw) && totalRaw > 0 ? Math.floor(totalRaw) : 0;
  const successRaw = Number(task?.successCount ?? 0);
  const failedRaw = Number(task?.failedCount ?? 0);
  const success = Number.isFinite(successRaw) ? Math.max(0, Math.floor(successRaw)) : 0;
  const failed = Number.isFinite(failedRaw) ? Math.max(0, Math.floor(failedRaw)) : 0;
  if (total <= 0) {
    return { total: 0, successCount: success, failedCount: failed };
  }
  return {
    total,
    successCount: Math.min(success, total),
    failedCount: Math.min(failed, Math.max(total - Math.min(success, total), 0)),
  };
}

/** 主图创建 / 图片处理等批量任务进度 */
export function getBatchTaskProgress(task) {
  if (!task) return { show: false };
  const { total, successCount, failedCount } = normalizeTaskCounters(task);
  const done = successCount + failedCount;
  const active = isTaskInProgress(task.status);

  if (!active) {
    return { show: false };
  }

  if (total <= 0) {
    return {
      show: true,
      percent: 0,
      indeterminate: true,
      label: task.status === 'waiting' ? tp('waitingExecution') : tp('executing'),
      detail: '',
    };
  }

  let percent = Math.round((done / total) * 100);
  if (done === 0) percent = 4;
  else if (done < total) percent = Math.min(99, Math.max(percent, 4));

  const detailParts = [tp('successCount', { count: successCount })];
  if (failedCount > 0) detailParts.push(tp('failedCount', { count: failedCount }));
  return {
    show: true,
    percent,
    indeterminate: false,
    label: `${done} / ${total}`,
    detail: task.status === 'waiting' ? tp('waitingExecution') : detailParts.join(' · '),
  };
}

/** 列表「完成情况」展示：含进行中、已完成、失败、已终止 */
export function getTaskCompletionDisplay(task) {
  if (!task) {
    return { showBar: false, variant: 'processing', statusLabel: '—', statusKind: 'none', percent: 0 };
  }

  const { total, successCount, failedCount } = normalizeTaskCounters(task);
  const done = successCount + failedCount;
  const status = String(task.status || '').toLowerCase();

  if (['succeed', 'success', 'completed'].includes(status)) {
    return {
      showBar: true,
      variant: 'success',
      statusLabel: tp('statusCompleted'),
      statusKind: 'success',
      percent: 100,
    };
  }

  if (status === 'failed') {
    let percent = total > 0 ? Math.round((done / total) * 100) : 0;
    if (percent <= 0 && total > 0) {
      percent = Math.max(1, Math.round((successCount / total) * 100));
    }
    return {
      showBar: true,
      variant: 'error',
      statusLabel: tp('failedGeneric'),
      statusKind: 'failed',
      percent,
    };
  }

  if (status === 'terminated') {
    const processed = total > 0 ? Math.min(done, total) : done;
    let percent = total > 0 ? Math.round((processed / total) * 100) : 0;
    if (processed > 0 && processed < total) {
      percent = Math.min(99, Math.max(percent, 4));
    }
    return {
      showBar: true,
      variant: 'error',
      statusLabel: tp('statusTerminated'),
      statusKind: 'terminated',
      percent,
    };
  }

  if (status === 'partial') {
    const processed = total > 0 ? Math.min(done, total) : done;
    let percent = total > 0 ? Math.round((processed / total) * 100) : 0;
    if (processed > 0 && processed < total) {
      percent = Math.min(99, Math.max(percent, 4));
    }
    return {
      showBar: true,
      variant: 'error',
      statusLabel: tp('partialFailed'),
      statusKind: 'partial',
      percent,
    };
  }

  if (['canceled', 'cancelled'].includes(status)) {
    const processed = total > 0 ? Math.min(done, total) : done;
    let percent = total > 0 ? Math.round((processed / total) * 100) : 0;
    if (processed > 0 && processed < total) {
      percent = Math.min(99, Math.max(percent, 4));
    }
    return {
      showBar: true,
      variant: 'error',
      statusLabel: tp('statusTerminated'),
      statusKind: 'terminated',
      percent,
    };
  }

  if (status === 'waiting') {
    return {
      showBar: true,
      variant: 'processing',
      statusLabel: tp('waitingExecution'),
      statusKind: 'waiting',
      percent: 0,
    };
  }

  if (!isTaskInProgress(status)) {
    return { showBar: false, variant: 'processing', statusLabel: '—', statusKind: 'none', percent: 0 };
  }

  if (total <= 0) {
    return {
      showBar: true,
      variant: 'processing',
      statusLabel: tp('processingGeneric'),
      statusKind: 'processing',
      percent: 0,
    };
  }

  let percent = Math.round((done / total) * 100);
  if (done === 0) {
    percent = 0;
  } else if (done < total) {
    percent = Math.min(99, Math.max(percent, 4));
  }

  return {
    showBar: true,
    variant: 'processing',
    statusLabel: tp('processingGeneric'),
    statusKind: 'processing',
    percent,
  };
}

/** 图片裁剪列表：状态文案 + 进度条下方数量说明 */
export function getCropTaskCompletionDisplay(task) {
  const base = getTaskCompletionDisplay(task);
  const { total, successCount, failedCount } = normalizeTaskCounters(task);
  const done = successCount + failedCount;
  const batchPhase = String(task?.result?.batchPhase || '').toLowerCase();
  const isUserCancelled = batchPhase === 'cancelled'
    || ['partial', 'canceled', 'cancelled'].includes(String(task?.status || '').toLowerCase());

  let statusLabel = base.statusLabel;
  let statusKind = base.statusKind;
  if (base.variant === 'success') {
    statusLabel = tp('cropDone');
    statusKind = 'success';
  } else if (isUserCancelled) {
    statusLabel = tp('cropTerminated');
    statusKind = 'terminated';
  } else if (base.variant === 'error') {
    if (base.statusKind === 'terminated') {
      statusLabel = tp('cropTerminated');
      statusKind = 'terminated';
    } else if (base.statusKind === 'partial') {
      statusLabel = tp('partialDone');
      statusKind = 'partial';
    } else {
      statusLabel = tp('failedGeneric');
      statusKind = 'failed';
    }
  } else if (base.statusKind === 'waiting') {
    statusLabel = tp('cropWaiting');
    statusKind = 'waiting';
  } else {
    statusLabel = tp('cropProcessing');
    statusKind = 'processing';
  }

  let detailLeft = '';
  if (base.variant === 'success') {
    detailLeft = total > 0
      ? tp('detailAllDoneImages', { count: formatLocalizedCount(total) })
      : tp('allDone');
  } else if (isUserCancelled) {
    detailLeft = total > 0
      ? tp('terminatedAt', { done: formatLocalizedCount(done), total: formatLocalizedCount(total) })
      : tp('terminatedShort');
  } else if (base.variant === 'error') {
    detailLeft = total > 0
      ? tp('interruptedAt', { done: formatLocalizedCount(done), total: formatLocalizedCount(total) })
      : tp('interruptedShort');
  } else if (total > 0) {
    detailLeft = tp('finishedOf', { done: formatLocalizedCount(done), total: formatLocalizedCount(total) });
  } else if (base.statusKind === 'waiting') {
    detailLeft = tp('waitingStart');
  } else {
    detailLeft = tp('processingShort');
  }

  return {
    ...base,
    showBar: base.variant === 'success' ? false : base.showBar,
    statusLabel,
    statusKind,
    detailLeft,
    total,
    done,
    successCount,
    failedCount,
  };
}

/** 取调用方传入的状态文案 key（兼容旧的中文 label 参数） */
function resolveStatusLabel(labels, name, fallbackKey) {
  const legacy = labels?.[`${name}StatusLabel`];
  if (legacy) return legacy;
  return tp(labels?.[`${name}StatusKey`] || fallbackKey);
}

/**
 * 图片裁剪同款进度条文案工厂（智能抠图 / 图裂变 / 万能改图 / 侵权检测等模块复用）
 */
export function buildTechTaskCompletionDisplay(task, labels = {}) {
  const base = getTaskCompletionDisplay(task);
  const { total, successCount, failedCount } = normalizeTaskCounters(task);
  const done = successCount + failedCount;
  const status = String(task?.status || '').toLowerCase();
  const isPartial = status === 'partial';
  const isCanceled = status === 'canceled' || status === 'cancelled';
  const isTerminated = status === 'terminated';
  const isPartialDone = isPartial && successCount > 0 && total > 0 && done >= total;
  const isPartialFailure = isPartial && total > 0 && done >= total;
  /** 非工作流模块历史数据：partial + 未跑满总数表示用户中途终止 */
  const isLegacyTerminatedPartial = isPartial && total > 0 && done < total;

  let statusLabel = base.statusLabel;
  let statusKind = base.statusKind;
  if (base.variant === 'success') {
    statusLabel = resolveStatusLabel(labels, 'success', 'doneGeneric');
    statusKind = 'success';
  } else if (base.variant === 'error') {
    if (isTerminated || isCanceled || isLegacyTerminatedPartial || base.statusKind === 'terminated') {
      statusLabel = resolveStatusLabel(labels, 'terminated', 'statusTerminated');
      statusKind = 'terminated';
    } else if (isPartialFailure || isPartialDone || base.statusKind === 'partial') {
      statusLabel = resolveStatusLabel(labels, 'partial', 'partialDone');
      statusKind = 'partial';
    } else {
      statusLabel = resolveStatusLabel(labels, 'failed', 'failedGeneric');
      statusKind = 'failed';
    }
  } else if (base.statusKind === 'waiting') {
    statusLabel = resolveStatusLabel(labels, 'waiting', 'waitingExecution');
    statusKind = 'waiting';
  } else {
    statusLabel = resolveStatusLabel(labels, 'processing', 'processingGeneric');
    statusKind = 'processing';
  }

  const successDetailKey = labels?.successDetailKey || 'detailAllDoneImages';

  let detailLeft = '';
  if (base.variant === 'success') {
    detailLeft = total > 0
      ? tp(successDetailKey, { count: formatLocalizedCount(total) })
      : tp('allDone');
  } else if (isPartialFailure || isPartialDone) {
    detailLeft = tp('successFailedSummary', {
      success: formatLocalizedCount(successCount),
      failed: formatLocalizedCount(failedCount),
    });
  } else if (base.variant === 'error') {
    detailLeft = total > 0
      ? tp('interruptedAt', { done: formatLocalizedCount(done), total: formatLocalizedCount(total) })
      : tp('interruptedShort');
  } else if (total > 0) {
    detailLeft = tp('finishedOf', { done: formatLocalizedCount(done), total: formatLocalizedCount(total) });
  } else if (base.statusKind === 'waiting') {
    detailLeft = tp('waitingStart');
  } else {
    detailLeft = tp('processingShort');
  }

  return {
    ...base,
    showBar: base.variant === 'success' ? false : base.showBar,
    statusLabel,
    statusKind,
    detailLeft,
    total,
    done,
    successCount,
    failedCount,
  };
}

export function getCutoutTaskCompletionDisplay(task) {
  return buildTechTaskCompletionDisplay(normalizeBatchProgressFromCounts(task), {
    successStatusKey: 'cutoutDone',
    terminatedStatusKey: 'cutoutTerminated',
    waitingStatusKey: 'cutoutWaiting',
    processingStatusKey: 'cutoutProcessing',
    failedStatusKey: 'failedGeneric',
  });
}

export function getFissionTaskCompletionDisplay(task) {
  return buildTechTaskCompletionDisplay(normalizeBatchProgressFromCounts(task), {
    successStatusKey: 'fissionDone',
    terminatedStatusKey: 'fissionTerminated',
    waitingStatusKey: 'fissionWaiting',
    processingStatusKey: 'fissionProcessing',
    failedStatusKey: 'failedGeneric',
  });
}

export function getImg2ImgTaskCompletionDisplay(task) {
  return buildTechTaskCompletionDisplay(normalizeBatchProgressFromCounts(task), {
    successStatusKey: 'generateDone',
    terminatedStatusKey: 'generateTerminated',
    waitingStatusKey: 'generateWaiting',
    processingStatusKey: 'generateProcessing',
    failedStatusKey: 'generateFailed',
  });
}

function hasCopywritingResultContent(task) {
  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  return Boolean(String(r.content || r.text || '').trim());
}

/** AI 文案：同步任务，有 content 即视为成功（避免落库失败误显示为失败） */
export function normalizeCopywritingProgressTask(task) {
  if (!task) return task;
  const base = normalizeBatchProgressFromCounts(task);
  if (!hasCopywritingResultContent(base)) return base;
  const status = String(base?.status || '').toLowerCase();
  const total = Number(base?.total ?? 1) || 1;
  if (status === 'failed' || isTaskInProgress(status)) {
    return {
      ...base,
      status: 'success',
      total,
      successCount: total,
      failedCount: 0,
    };
  }
  return base;
}

export function getAiCopywritingTaskCompletionDisplay(task) {
  const normalized = normalizeCopywritingProgressTask(task);
  return buildTechTaskCompletionDisplay(normalized, {
    successStatusKey: 'generateDone',
    terminatedStatusKey: 'generateTerminated',
    waitingStatusKey: 'generateWaiting',
    processingStatusKey: 'generateProcessing',
    failedStatusKey: 'generateFailed',
    successDetailKey: 'detailAllDoneItems',
  });
}

export function getGoodsTaskCompletionDisplay(task) {
  return buildTechTaskCompletionDisplay(normalizeBatchProgressFromCounts(task), {
    successStatusKey: 'createDone',
    terminatedStatusKey: 'createTerminated',
    waitingStatusKey: 'createWaiting',
    processingStatusKey: 'createProcessing',
    failedStatusKey: 'failedGeneric',
  });
}

export function mapInfringementRowToProgressTask(row) {
  if (!row) return null;
  return normalizeBatchProgressFromCounts({
    status: row.status,
    total: row.totalCount,
    successCount: row.successCount,
    failedCount: row.failedCount,
    createdAt: row.createdAt,
    operator: row.operatorName,
    createdBy: row.operatorName,
  });
}

/** 计数已满但 status 仍为进行中时，按完成态展示进度条 */
export function normalizeBatchProgressFromCounts(task) {
  if (!task) return task;
  const total = Number(task.total ?? task.totalCount ?? 0);
  if (total <= 0) return task;
  const successCount = Number(task.successCount ?? 0) || 0;
  const failedCount = Number(task.failedCount ?? 0) || 0;
  const done = successCount + failedCount;
  if (done < total) return task;
  const status = String(task.status || '').toLowerCase();
  if (!isTaskInProgress(status)) return task;
  if (failedCount >= total) return { ...task, status: 'failed' };
  if (failedCount > 0) return { ...task, status: 'partial' };
  return { ...task, status: 'completed' };
}

export function getInfringementTaskCompletionDisplay(taskOrRow) {
  const task = taskOrRow?.totalCount != null ? mapInfringementRowToProgressTask(taskOrRow) : taskOrRow;
  return buildTechTaskCompletionDisplay(normalizeBatchProgressFromCounts(task), {
    successStatusKey: 'detectDone',
    terminatedStatusKey: 'detectTerminated',
    waitingStatusKey: 'detectWaiting',
    processingStatusKey: 'detectProcessing',
    failedStatusKey: 'detectFailed',
  });
}

function mapWorkflowStatusToProgressStatus(status) {
  switch (status) {
    case 'queued':
    case 'loading':
      return 'waiting';
    case 'running':
      return 'processing';
    case 'done':
      return 'completed';
    case 'partial':
      return 'partial';
    case 'failed':
      return 'failed';
    case 'terminated':
      return 'terminated';
    case 'pendingReview':
      return 'waiting';
    default:
      return 'processing';
  }
}

function countWorkflowTaskResults(task) {
  const { total, successCount, failedCount, partialCount } = countWorkflowTaskOutcome(task);
  return { total, successCount, failedCount: failedCount + partialCount };
}

export function mapWorkflowTaskToProgressTask(task) {
  if (!task) return null;
  const { total, successCount, failedCount } = countWorkflowTaskResults(task);
  return normalizeBatchProgressFromCounts({
    status: mapWorkflowStatusToProgressStatus(resolveWorkflowTaskDisplayStatus(task)),
    total,
    successCount,
    failedCount,
    createdAt: task.createdAt,
  });
}

export function getWorkflowTaskCompletionDisplay(task) {
  const labels = {
    successStatusKey: 'execDone',
    partialStatusKey: 'execPartial',
    terminatedStatusKey: 'statusTerminated',
    waitingStatusKey: 'execWaiting',
    processingStatusKey: 'execRunning',
    failedStatusKey: 'execFailed',
  };
  const resolvedStatus = resolveWorkflowTaskDisplayStatus(task);
  if (resolvedStatus === 'queued') {
    labels.waitingStatusKey = 'queuedLabel';
  } else if (resolvedStatus === 'loading') {
    labels.waitingStatusKey = 'loadingLabel';
  } else if (resolvedStatus === 'pendingReview') {
    labels.waitingStatusKey = 'pendingReviewLabel';
  }
  return buildTechTaskCompletionDisplay(mapWorkflowTaskToProgressTask(task), labels);
}

/** 工作流任务进度（按条数 × 步骤估算；阶段优先时同一模块多条会并行推进） */
export function getWorkflowTaskProgress(task) {
  if (!task) return { show: false };
  // 用纠正后的展示态，刷新未 hydrate 完时不要把残留 running 画成「处理中」
  const status = resolveWorkflowTaskDisplayStatus(task) || task.status;

  if (
    status === 'terminated'
    || status === 'done'
    || status === 'success'
    || status === 'partial'
    || status === 'failed'
  ) {
    return { show: false };
  }

  if (status === 'loading') {
    return {
      show: true,
      percent: 0,
      indeterminate: true,
      label: tp('workflowLoading'),
      detail: tp('workflowSyncing'),
    };
  }

  if (status === 'queued') {
    return {
      show: true,
      percent: 0,
      indeterminate: true,
      label: tp('queuedLabel'),
      detail: tp('workflowWaitingSlot'),
    };
  }

  if (status === 'pendingReview') {
    const reviewLabel = task.pendingReview?.stepLabel || task.progress?.stepLabel || '';
    return {
      show: true,
      percent: 100,
      indeterminate: false,
      label: tp('pendingReviewLabel'),
      detail: reviewLabel
        ? tp('workflowReviewDetail', { step: reviewLabel })
        : tp('workflowReviewPending'),
    };
  }

  if (status !== 'running') {
    return { show: false };
  }

  const prog = task.progress || {};
  const rowIndex = Number(prog.rowIndex) || 0;
  const rowTotal = Number(prog.rowTotal) || task.rows?.length || 0;
  const stepIndex = Number(prog.stepIndex) || 0;
  const stages = resolveTemplateStepStages(task.templateSnapshot || {});
  const flatSteps = stages.flat().filter((s) => s.navId !== 'sourcingHub');
  const stepCount = Math.max(flatSteps.length, (task.templateSnapshot?.steps || []).filter((s) => s.navId !== 'sourcingHub').length, 1);
  const stepDetail = prog.stepLabel || task.live?.lastMessage || '';

  const entries = getWorkflowTaskResultEntries(task);
  const inProgressRows = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry?.result?.inProgress);
  const completedRows = entries.filter((entry) => entry?.result?.ok && !entry?.result?.inProgress).length;
  const terminalFailedRows = entries.filter(
    (entry) => entry?.result && !entry.result.inProgress && entry.result.ok === false,
  ).length;
  const finishedRows = completedRows + terminalFailedRows;

  if (rowTotal <= 0) {
    return {
      show: true,
      percent: 4,
      indeterminate: true,
      label: tp('executing'),
      detail: stepDetail,
    };
  }

  const totalUnits = rowTotal * stepCount;
  const inProgressProgress = inProgressRows.reduce((sum, { entry }) => {
    const stepsDone = Array.isArray(entry?.result?.steps) ? entry.result.steps.length : 0;
    const rowStepIndex = Math.min(stepsDone, stepCount - 1);
    return sum + rowStepIndex + 0.35;
  }, 0);
  const currentUnits = finishedRows * stepCount + (inProgressRows.length > 0 ? inProgressProgress : Math.min(stepIndex + 0.4, stepCount));
  let percent = Math.round((currentUnits / totalUnits) * 100);
  percent = Math.max(4, Math.min(99, percent));

  let label;
  if (inProgressRows.length > 1) {
    label = tp('workflowParallel', { count: inProgressRows.length });
  } else if (inProgressRows.length === 1) {
    label = tp('workflowRowOf', { index: inProgressRows[0].index + 1, total: rowTotal });
  } else {
    label = tp('workflowRowOf', { index: Math.min(rowIndex + 1, rowTotal), total: rowTotal });
  }

  const detailParts = [];
  if (finishedRows > 0) detailParts.push(tp('workflowFinishedRows', { done: finishedRows, total: rowTotal }));
  if (stepDetail) detailParts.push(stepDetail);
  const detail = detailParts.join(' · ') || stepDetail;

  return {
    show: true,
    percent,
    indeterminate: inProgressRows.length === 0 && finishedRows === 0,
    label,
    detail,
  };
}

/** 已成功跑完的工作流条数（终止时正在处理的那一条不计入） */
export function getWorkflowCompletedRowCount(task) {
  if (!task) return 0;
  const fromSummary = Number(task.summary?.okCount);
  if (Number.isFinite(fromSummary) && fromSummary >= 0) return fromSummary;
  const results = task.summary?.results || task.results;
  if (Array.isArray(results)) {
    return results.filter((x) => x?.result?.ok).length;
  }
  return Math.max(0, Number(task.resumeFromRowIndex) || 0);
}

/** 当前正在处理或终止时所在的条数（1-based） */
export function getWorkflowCurrentRowOneBased(task) {
  if (!task) return 0;
  const total = task.rows?.length || 0;
  const progIdx = Number(task.progress?.rowIndex);
  if (Number.isFinite(progIdx) && progIdx >= 0) {
    return Math.min(progIdx + 1, total || progIdx + 1);
  }
  return getWorkflowCompletedRowCount(task);
}
